import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorBox, Loading, useBusy } from '../ui';
import { useSession } from '../session';
import { BrandLogo } from '../brand';
import { ThemeToggle } from '../theme';

export interface AuthConfig {
  googleClientId: string | null;
  devLogin: boolean;
  devInbox: boolean;
  smsProvider: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (o: { client_id: string; callback: (r: { credential: string }) => void; nonce: string; ux_mode?: string }) => void;
          renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
        };
      };
    };
  }
}

let googleLibrary: Promise<void> | null = null;

function loadGoogleIdentity(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!googleLibrary) {
    googleLibrary = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        googleLibrary = null;
        reject(new Error('Could not load Google sign-in'));
      };
      document.head.appendChild(script);
    });
  }
  return googleLibrary;
}

export function GoogleButton({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const doneRef = useRef(onDone);
  const [error, setError] = useState<unknown>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => { doneRef.current = onDone; }, [onDone]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadGoogleIdentity();
      if (cancelled) return;
      const { nonce } = await api.post<{ nonce: string }>('/api/auth/google/nonce', {}, { orgId: null });
      if (cancelled || !ref.current || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        nonce,
        callback: async ({ credential }) => {
          try {
            await api.post('/api/auth/google', { credential }, { orgId: null });
            doneRef.current();
          } catch (e) {
            setError(e);
          }
        },
      });
      window.google.accounts.id.renderButton(ref.current, { theme: 'outline', size: 'large', width: 300, text: 'continue_with' });
      setReady(true);
    })().catch((e) => { if (!cancelled) setError(e); });
    return () => {
      cancelled = true;
    };
  }, [clientId]);
  return (
    <div>
      {/* Same size as Google's button, so the page does not jump when it arrives. */}
      <div className="google-slot">
        <div ref={ref} className="google-target" />
        {!ready && !error ? <div className="google-placeholder" aria-hidden="true">Loading Google sign-in…</div> : null}
      </div>
      <ErrorBox error={error} />
    </div>
  );
}

/** Google refuses sign-in inside app web views (WhatsApp, Instagram, Facebook…). */
export function isInAppBrowser(ua = navigator.userAgent): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Snapchat|Twitter|; wv\)/i.test(ua);
}

/** The single way to sign in: Google, or a hint to open the page in a real browser. */
export function GoogleSignIn({ config, onDone }: { config: AuthConfig; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  if (!config.googleClientId) return <p className="alert warn">Sign-in is not set up on this server yet.</p>;
  if (isInAppBrowser()) {
    return (
      <div className="alert info stack">
        <p>Google sign-in does not work inside this app. Open this page in Chrome or Safari to continue.</p>
        <button
          type="button"
          className="btn block"
          onClick={() => void navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true), () => undefined)}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      </div>
    );
  }
  return <GoogleButton clientId={config.googleClientId} onDone={onDone} />;
}

export function useAuthConfig() {
  return useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<AuthConfig>('/api/auth/config', { orgId: null }) });
}

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { me, loading, refresh } = useSession();
  const cfg = useAuthConfig();
  const requested = new URLSearchParams(loc.search).get('next') || '/app';
  const next = requested.startsWith('/') && !requested.startsWith('//') && !requested.includes('\\') && !/^\/login(?:[/?#]|$)/.test(requested) ? requested : '/app';

  const done = async () => {
    await refresh();
    nav(next, { replace: true });
  };

  if (loading) return <Loading />;
  if (me) return <Navigate to={me.station ? '/station' : next} replace />;

  return (
    <div className="login-wrap">
      <div className="login-tools"><ThemeToggle /></div>
      <div className="card stack">
        <div className="brand-heading"><BrandLogo size={64} /><h1>SuperIndent</h1></div>
        <p className="brand-tagline">Fuel indents made simple</p>
        {cfg.isLoading ? <Loading /> : <ErrorBox error={cfg.error} onRetry={() => cfg.refetch()} />}
        {cfg.data ? <GoogleSignIn config={cfg.data} onDone={() => void done()} /> : null}
        <p className="muted">New fleet or petrol pump? Continue with Google to register your business. Staff: sign in with Google, then confirm the mobile number your business added.</p>
      </div>
      <nav className="row public-nav login-links" aria-label="About and privacy">
        <Link to="/">Home</Link>
        <Link to="/about">About SuperIndent</Link>
        <Link to="/privacy">Privacy policy</Link>
      </nav>
      {cfg.data?.devLogin ? <DemoLogin onDone={() => void done()} /> : null}
      {cfg.data?.devInbox ? <p className="footer-note"><a href="/dev/inbox">Local SMS inbox (development only)</a></p> : null}
    </div>
  );
}

function DemoLogin({ onDone }: { onDone: () => void }) {
  const users = useQuery({ queryKey: ['dev-users'], queryFn: () => api.get<{ id: string; name: string; roles: string[] }[]>('/api/auth/dev-users', { orgId: null }) });
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <h2>Demo sign-in (local only)</h2>
      <p className="muted">Fictional demo users. This box never appears in production.</p>
      <ul className="list">
        {users.data?.map((u) => (
          <li key={u.id} className="row between">
            <div>
              <strong>{u.name}</strong>
              <div><small>{u.roles.join(' · ')}</small></div>
            </div>
            <button className="btn small" disabled={busy} onClick={() => void run(async () => { await api.post('/api/auth/dev-login', { userId: u.id }, { orgId: null }); onDone(); })}>
              Sign in
            </button>
          </li>
        ))}
      </ul>
      <ErrorBox error={error} />
    </div>
  );
}
